import type { FillOptions, SymmetryAxis, XY } from "@pdfking/filling"
import type { BestFill, FillStrategyData } from "@pdfking/filling"
import { BLANK_MATRIX, GetBestFill, xy_len } from "@pdfking/filling"
import type { Distance, Margins, Rectangle } from "@pdfking/math"
import { SumMargins } from "@pdfking/math"
import { QUARTER_TURN, ZERO_DEGREE, ZERO_MARGINS } from "@pdfking/math/constants"
import { NullQ } from "@pdfking/structs"
import { Range } from "@pdfking/tfunc"

import { ALONG_AXIS } from "../data/AlongAxis"
import type { FillingId } from "../types/filling"
import type {
  FilledLayout,
  FillSides,
  Layout,
  LayoutDirection,
  LayoutGenerator,
  LayoutObject,
} from "../types/layout"
import { FlipLayout } from "./FlipLayout"
import { PageObject } from "./PageObject"
import { RotateLayout } from "./RotateLayout"

/** A place in the grid, before a layout is put in it. */
type Slot = { block: number; rotated: boolean }

/** A grid of slots: `x` columns of `y` cells each. */
type Block = { matrix: XY; slots: Slot[] }

const ACROSS: Record<SymmetryAxis, SymmetryAxis> = { x: "y", y: "x" }

/** The fill stands the same both sides of its middle across `axis`: an even
 * count there, and no turned strip splitting it. */
const splits = (fill: FillStrategyData, axis: SymmetryAxis): boolean =>
  fill.subjects[axis] % 2 === 0 &&
  !xy_len(fill.subjects_flipped[axis]) &&
  fill.subjects_flipped[ACROSS[axis]][axis] % 2 === 0

/**
 * The axis the fill turns over on, read off its shape: a fill measured on
 * half the sheet turns over where it doubles; any other splits in two where
 * its counts do, trying its own `use_symmetry` first (already turned with the
 * sheet when the fill was found on the turned one).
 */
const flip_axis = (
  fill: FillStrategyData,
  grown: SymmetryAxis | undefined
): SymmetryAxis | undefined => {
  if (!fill.use_symmetry) return undefined
  if (grown) return grown
  const order: SymmetryAxis[] = fill.use_symmetry === "y" ? ["y", "x"] : ["x", "y"]
  return order.find(axis => splits(fill, axis))
}

const scale = (matrix: XY, axis: SymmetryAxis | undefined, by: number): XY =>
  axis ? { ...matrix, [axis]: matrix[axis] * by } : matrix

const block = (matrix: XY, index: number, rotated: boolean): Block => ({
  matrix,
  slots: [...Range(xy_len(matrix))].map(() => ({ block: index, rotated })),
})

// Every page under `layout` cut with `block`: a page already in a block of
// its own — a filled sheet's grid, tiled into a bigger one — keeps it apart
// from the outer blocks by folding the two together.
const blocked = <D>(layout: Layout<D>, block: number): Layout<D> =>
  layout.enum === "page"
    ? { ...layout, block: (layout.block ?? 0) * 2 + block }
    : { ...layout, layouts: layout.layouts?.map(child => blocked(child, block)) }

/**
 * Puts `object` in a slot: a page takes the fill's padding as its own, a
 * layout grows by it, and a slot of the turned strip turns what it holds.
 * Either way what it holds is cut with the slot's block.
 */
const place = <D>(object: LayoutObject<D>, slot: Slot, padding: Margins<Distance>): Layout<D> => {
  const layout: Layout<D> =
    object.layout.enum === "page"
      ? { ...object.layout, block: slot.block, padding }
      : blocked(
          {
            ...object.layout,
            padding: SumMargins(object.layout.padding ?? ZERO_MARGINS, padding),
            size: {
              height: (object.size.height + padding.top + padding.bottom) as Distance,
              width: (object.size.width + padding.left + padding.right) as Distance,
            },
          },
          slot.block
        )
  return RotateLayout(layout, slot.rotated ? QUARTER_TURN : ZERO_DEGREE)
}

/**
 * The lines of a block, stacked along `direction`: on a row every line is a
 * column of cells, on a column every line is a row. `cells` come column by
 * column, top to bottom — the order the imposition numbers them in. A place
 * left out closes its line up, and a line left with nothing is dropped.
 */
const lines = <D>(
  matrix: XY,
  direction: LayoutDirection,
  cells: (Layout<D> | undefined)[]
): Layout<D>[] =>
  (direction === "row"
    ? [...Range(matrix.x)].map(x => ({
        direction: "column" as const,
        enum: "flex" as const,
        layouts: cells.slice(x * matrix.y, (x + 1) * matrix.y).filter(cell => !NullQ(cell)),
      }))
    : [...Range(matrix.y)].map(y => ({
        direction: "row" as const,
        enum: "flex" as const,
        layouts: [...Range(matrix.x)]
          .map(x => cells[x * matrix.y + y])
          .filter(cell => !NullQ(cell)),
      }))
  ).filter(line => line.layouts.length)

type FlexLayout<D> = Extract<Layout<D>, { enum: "flex" }>

// Every page of `layout` on the rear, where it stands.
const rear = <D>(layout: Layout<D>): Layout<D> =>
  layout.enum === "page"
    ? { ...layout, is_front: false }
    : { ...layout, layouts: layout.layouts?.map(rear) }

const children = <D>(layout: Layout<D>): Layout<D>[] =>
  layout.enum === "flex" ? (layout.layouts ?? []) : [layout]

// The fill and its turned-over half, as one block: stacked along their own
// direction the lines run on, stacked across it every line runs on into its
// mirror — never two blocks, each packed apart with a cut between them. The
// mirror trails, or leads when `rear_leads`.
const stack = <D>(
  axis: SymmetryAxis,
  first: FlexLayout<D>,
  second: Layout<D>,
  rear_leads: boolean
): FlexLayout<D> => {
  const lines = children(second)
  const pair = <T>(front: T[], rear: T[]): T[] =>
    rear_leads ? [...rear, ...front] : [...front, ...rear]
  return {
    ...first,
    layouts:
      (first.direction ?? "row") === ALONG_AXIS[axis]
        ? pair(children(first), lines)
        : children(first).map((line, i) => ({
            ...line,
            layouts: pair(children(line), children(lines[i]!)),
          })),
  }
}

/**
 * Fills `canvas` with objects of `size`: the search settles how many fit, and
 * `generate` makes what goes in each place it finds, numbered from 1 in the
 * order the imposition numbers them. Left out, every place is a page of its
 * own.
 *
 * On `both` sides a symmetric fill numbers only one half, and the other is
 * that half turned over: the same pages, on the rear. On one side nothing is
 * turned over, and every place is numbered — twice the pages, all on that
 * side. The answer is an object of the fill's size, so it can be filled into a
 * bigger sheet in its turn.
 */
export const FillLayouts = <D = never>(
  canvas: Parameters<typeof GetBestFill>[0],
  size: Rectangle<Distance>,
  {
    fill: found,
    filling,
    generate = PageObject,
    sides = "both",
    ...search
  }: {
    /** The search's answer, when the caller already has it; the search is
     * skipped and the rest of the options are what it was made with. */
    fill?: BestFill
    filling?: FillingId
    generate?: LayoutGenerator<D>
    sides?: FillSides
  } & FillOptions = {
    use_symmetry: undefined,
  }
): FilledLayout<D> => {
  const fill = found ?? GetBestFill(canvas, size, search)
  // A fill measured on half the sheet stands twice on it: turned over, its
  // other half is the rear, and otherwise it is one grid twice as long. One
  // that is symmetric of itself has its half cut out of it here.
  const grown: SymmetryAxis | undefined = fill.symmetric_grow.x
    ? "x"
    : fill.symmetric_grow.y
      ? "y"
      : undefined
  const used = sides === "both" ? flip_axis(fill, grown) : undefined
  const halved = grown ? undefined : used
  const doubled = used ? undefined : grown
  const counts = (matrix: XY): XY => scale(scale(matrix, halved, 0.5), doubled, 2)

  // The turned strip lies beside the upright grid or under it, never both.
  const strip: SymmetryAxis | undefined = xy_len(fill.subjects_flipped.x)
    ? "x"
    : xy_len(fill.subjects_flipped.y)
      ? "y"
      : undefined
  const direction = ALONG_AXIS[strip ?? "x"]
  const normal = block(counts(fill.subjects), 0, false)
  const turned = block(strip ? counts(fill.subjects_flipped[strip]) : BLANK_MATRIX, 1, true)

  const assigned = [...normal.slots, ...turned.slots].map((slot, i) => {
    const object = generate(BigInt(i + 1), size)
    return object && place(object, slot, fill.padding)
  })
  const turned_lines = lines(turned.matrix, direction, assigned.slice(normal.slots.length))
  const normal_lines = lines(normal.matrix, direction, assigned.slice(0, normal.slots.length))

  // The strip trails the grid: right of it on a row, under it on a column.
  let layout: FlexLayout<D> = {
    direction,
    enum: "flex",
    layouts: [...normal_lines, ...turned_lines],
  }
  // The front half stands right, or at the bottom, of the sheet it prints on. A
  // fill measured on the turned sheet is stood back up a quarter clockwise,
  // which takes its bottom to the sheet's left: turned over on y, the front leads.
  const on_turned_sheet = fill.width !== canvas.width
  const rear_leads = (axis: SymmetryAxis) => !(on_turned_sheet && axis === "y")
  if (grown && used) layout = stack(grown, layout, FlipLayout(layout, grown), rear_leads(grown))
  if (halved) layout = stack(halved, layout, FlipLayout(layout, halved), rear_leads(halved))

  if (sides === "rear") layout = { ...layout, layouts: layout.layouts?.map(rear) }

  const filled = { height: fill.height, width: fill.width }
  return {
    fill,
    is_front: sides !== "rear",
    // A fill turned over onto itself must stand the same both sides of its
    // middle: packed into a corner it would not, so compact centres instead.
    layout: {
      ...layout,
      fill_mode: used && filling === "compact" ? "center" : filling,
      padding: fill.security,
      size: filled,
    },
    size: filled,
  }
}
